import { NotificationEventEnum } from '.'
import { CASStatus } from '../statuses/cas'
import { NotificationEventItem } from './NotificationEventItem'
import {
  AllApprovalsNotificationEventItem,
  PartialApprovalNotificationEventItem,
} from './common/approval'
import { CommentedNotificationEventItem } from './common/commented'
import { DispositionGivenNotificationEventItem } from './common/disposition'
import { EditedNotificationEventItem } from './common/edited'
import { NumberNameEditedNotificationEventItem } from './common/numberName'
import { RevertedNotificationEventItem } from './common/reverted'

const cas = 'Construction & Admin Submittal' as const

const Submitted = new NotificationEventItem({
  id: '5c374e10-abbe-4c85-a09b-4a4d042449b3',
  name: 'Submitted',
  description: `A new ${cas} is submitted for the workflow`,
})
const Resubmitted = new NotificationEventItem({
  id: '2d78bc2d-97aa-4fa6-9da1-72f8a0155312',
  name: 'Resubmitted',
  description: `An existing ${cas} is resubmitted for the workflow`,
})
const RoutedToGroupReview = new NotificationEventItem({
  id: 'f0d6fe26-802a-42f7-a5df-fb8526dbf518',
  name: 'Routed to Group Review',
  description: `A router determines the ${cas} is ready for ${CASStatus.GroupReview.name}`,
})
const RoutingGroupAccepted = PartialApprovalNotificationEventItem({
  id: '6ab3b6fc-cb4b-41a9-8e7f-22cbc741db33',
  name: 'Routing Group Accepted',
  description: { object: cas, group: 'Routing Group' },
})
const AllRoutingGroupsAccepted = AllApprovalsNotificationEventItem({
  id: '19c66e03-bdd6-4840-8c79-04df8057d61f',
  name: 'All Routing Groups Accepted',
  description: { object: cas, group: 'Routing Group' },
})
const RoutedToREReview = new NotificationEventItem({
  id: 'ddef5b86-dfaf-4342-8af9-166d4a54e8c7',
  name: 'Routed to RE Review',
  description: `A router determines the ${cas} is ready for ${CASStatus.REReview.name}`,
})
const DispositionGiven = DispositionGivenNotificationEventItem({
  id: 'b1bfa167-a75b-4a8c-b9b7-2aac58a98516',
  submittal: cas,
})
const Reverted = RevertedNotificationEventItem({
  id: '9e77ae93-ae1c-4d13-a248-145fdbde421f',
  revertable: cas,
  from: [
    CASStatus.Closed,
    CASStatus.Unlocked,
    CASStatus.GroupReview,
    CASStatus.REReview,
  ],
  to: [CASStatus.RouterReview, CASStatus.GroupReview, CASStatus.REReview],
})
const RevisionsRequested = new NotificationEventItem({
  id: '12e6bdab-c940-4f3d-b364-f98ff573f400',
  name: 'Revisions Requested',
  description: `A ${cas} does not meet expectations in its current form`,
})
const NumberOrNameEdited = NumberNameEditedNotificationEventItem({
  id: '4a3742ec-1815-4e0f-9907-7ac4ef911107',
})
const Edited = EditedNotificationEventItem({
  id: '48076a04-8dea-4d62-a4d6-eba5afd4bc16',
  editable: cas,
})
const Commented = CommentedNotificationEventItem({
  id: '3f857e2d-df24-411b-9da5-d5542732e389',
  commentable: cas,
})

const RequestedReview = new NotificationEventItem({
  id: 'fdf0bcdc-f330-4cd3-8e55-78577254cc82',
  name: 'Requested Review Upcoming',
  description: `The Requested Review Date of a ${cas} has reached a notification threshold`,
})

export type CASNotificationEventItem =
  | typeof Submitted
  | typeof Resubmitted
  | typeof RoutedToGroupReview
  | typeof RoutingGroupAccepted
  | typeof AllRoutingGroupsAccepted
  | typeof RoutedToREReview
  | typeof DispositionGiven
  | typeof Reverted
  | typeof RevisionsRequested
  | typeof NumberOrNameEdited
  | typeof Edited
  | typeof Commented
  | typeof RequestedReview

class CASNotificationEventEnum extends NotificationEventEnum<
  'CASNotificationEvent',
  CASNotificationEventItem,
  CASNotificationEventItem['name'],
  CASNotificationEventItem['id']
> {
  readonly typename = 'CASNotificationEvent'
  get values() {
    return [
      this.Submitted,
      this.Resubmitted,
      this.RoutedToGroupReview,
      this.RoutingGroupAccepted,
      this.AllRoutingGroupsAccepted,
      this.RoutedToREReview,
      this.DispositionGiven,
      this.Reverted,
      this.RevisionsRequested,
      this.NumberOrNameEdited,
      this.Edited,
      this.Commented,
      this.RequestedReview,
    ]
  }
  readonly Submitted = Submitted
  readonly Resubmitted = Resubmitted
  readonly RoutedToGroupReview = RoutedToGroupReview
  readonly RoutingGroupAccepted = RoutingGroupAccepted
  readonly AllRoutingGroupsAccepted = AllRoutingGroupsAccepted
  readonly RoutedToREReview = RoutedToREReview
  readonly DispositionGiven = DispositionGiven
  readonly Reverted = Reverted
  readonly RevisionsRequested = RevisionsRequested
  readonly NumberOrNameEdited = NumberOrNameEdited
  readonly Edited = Edited
  readonly Commented = Commented
  readonly RequestedReview = RequestedReview
}

export const CASNotificationEvent = new CASNotificationEventEnum()
